import { useMemo, useState } from "react";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { ForecastRuleFields } from "./ForecastRuleFields";
import { useToast } from "hooks/useToast";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import {
  useCreateForecastSettingsMutation,
  useUpdateForecastSettingsMutation,
} from "store/apis/reports";
import { ForecastRule, ForecastSettings } from "types/Models/report";
import { BackendError } from "types/utils/error";
import {
  FORMULA,
  FORMULA_NEEDS_VALUE,
  FORMULA_NEEDS_DATE_WINDOW,
  FORMULA_IS_LAST_6M,
  FORMULA_INFO,
  FormulaValue,
} from "constants/forecast";
import dayjs from "dayjs";
import { YYYY_MM_DD } from "constants/date";
import { FormikForm } from "components/FormikForm/FormikForm";
import { object, string } from "yup";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { TxnCategories } from "types/Models/books";
import { ArrowRight } from "components/icons/ArrowRight";
import classNames from "classnames";
import { RETAINED_EARNING_IDENTIFIER } from "constants/categoryIdentifier";
import { motion, AnimatePresence } from "framer-motion";
import Async from "components/DesignSystem/AsyncComponents/Async";
import ToolTip from "components/design/toolTip";
import { SmallInfo } from "components/icons/SmallInfo";
import { BulbIcon } from "components/icons/BulbIcon";
import { useWindowSize } from "hooks/useWindowSize";

type RuleRow = {
  categoryUuid: string;
  formula: FormulaValue;
  value: string;
  startDate: string;
  endDate: string;
};

type FormValues = {
  name: string;
  rules: Record<string, RuleRow>;
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (setting: ForecastSettings) => void;
  editingSet?: ForecastSettings | null;
  highlightedCategoryUuid?: string | null;
};

const HIDDEN_CATEGORY_TYPES = ["BANK_TRANSFER", "PAY_DOWN_CREDIT"] as const;

const isRuleRestrictedCategory = (category: TxnCategories): boolean =>
  String(category.identifier) === RETAINED_EARNING_IDENTIFIER;

const validationSchema = object({ name: string().required() });

const last6MonthsRange = () => {
  const end = dayjs().subtract(1, "month").endOf("month");
  const start = end.subtract(5, "month").startOf("month");
  return { start: start.format(YYYY_MM_DD), end: end.format(YYYY_MM_DD) };
};

const makeNoRule = (categoryUuid: string): RuleRow => ({
  categoryUuid,
  formula: FORMULA.NO_RULE,
  value: "",
  startDate: "",
  endDate: "",
});

const makeDefaultRule = (categoryUuid: string): RuleRow => ({
  categoryUuid,
  formula: FORMULA.GROWTH_PCT,
  value: "1",
  startDate: "",
  endDate: "",
});
const ruleToRow = (rule: ForecastRule): RuleRow => ({
  categoryUuid: rule.category_uuid,
  formula: rule.formula as FormulaValue,
  value: rule.value ?? "",
  startDate: rule.start_date ?? "",
  endDate: rule.end_date ?? "",
});

const resolveDateRange = (r: RuleRow) => {
  if (FORMULA_NEEDS_DATE_WINDOW.has(r.formula)) {
    return { startDate: r.startDate || null, endDate: r.endDate || null };
  }
  if (FORMULA_IS_LAST_6M.has(r.formula)) {
    const range = last6MonthsRange();
    return { startDate: range.start, endDate: range.end };
  }
  return { startDate: null, endDate: null };
};

const buildRulePayload = (r: RuleRow) => {
  const { startDate, endDate } = resolveDateRange(r);
  return {
    category_uuid: r.categoryUuid,
    formula: r.formula,
    value: FORMULA_NEEDS_VALUE.has(r.formula) && r.value ? r.value : undefined,
    start_date: startDate,
    end_date: endDate,
  };
};

const collectDescendantUuids = (categories: TxnCategories[]): string[] =>
  categories.flatMap((cat) => [
    cat.uuid,
    ...collectDescendantUuids(cat.types ?? []),
  ]);

const buildTreeMaps = (categories: TxnCategories[]) => {
  const descendantsMap = new Map<string, string[]>();
  const ancestorsMap = new Map<string, string[]>();

  const walk = (items: TxnCategories[], parentChain: string[]) => {
    for (const cat of items) {
      ancestorsMap.set(cat.uuid, [...parentChain]);
      descendantsMap.set(cat.uuid, collectDescendantUuids(cat.types ?? []));
      walk(cat.types ?? [], [...parentChain, cat.uuid]);
    }
  };

  walk(categories, []);
  return { descendantsMap, ancestorsMap };
};

const resolveInitialRule = (
  cat: TxnCategories,
  existingByUuid: Map<string, ForecastRule>,
  isEditing: boolean
): RuleRow => {
  if (isRuleRestrictedCategory(cat)) return makeNoRule(cat.uuid);

  const existing = existingByUuid.get(cat.uuid);
  if (existing) return ruleToRow(existing);
  if (isEditing) return makeNoRule(cat.uuid);
  return makeDefaultRule(cat.uuid);
};

const getInitialRules = (
  categories: TxnCategories[],
  editingSet?: ForecastSettings | null
): Record<string, RuleRow> => {
  const rules: Record<string, RuleRow> = {};
  const isEditing = Boolean(editingSet);
  const existingByUuid = new Map<string, ForecastRule>(
    editingSet?.rules.map((r) => [r.category_uuid, r]) ?? []
  );

  const walk = (items: TxnCategories[]) => {
    for (const cat of items) {
      rules[cat.uuid] = resolveInitialRule(cat, existingByUuid, isEditing);
      walk(cat.types ?? []);
    }
  };

  walk(categories);
  return rules;
};

export const NewAssumptionSetModal = ({
  isOpen,
  onClose,
  onCreated,
  editingSet,
  highlightedCategoryUuid,
}: Props) => {
  const entityId = useCurrentEntityId();
  const { alertToast, successToast } = useToast();
  const isEditMode = Boolean(editingSet);

  const { chartOfAccounts, isLoading: isLoadingCOA } = useChartOfAccounts({
    hiddenCategoryTypes: [...HIDDEN_CATEGORY_TYPES],
  });

  const [createForecastSettings, { isLoading: isCreating }] =
    useCreateForecastSettingsMutation();
  const [updateForecastSettings, { isLoading: isUpdating }] =
    useUpdateForecastSettingsMutation();
  const isLoading = isCreating || isUpdating;

  const { descendantsMap, ancestorsMap } = useMemo(
    () => buildTreeMaps(chartOfAccounts),
    [chartOfAccounts]
  );

  const expandedUuids = useMemo(
    () =>
      new Set(
        highlightedCategoryUuid
          ? ancestorsMap.get(highlightedCategoryUuid) ?? []
          : []
      ),
    [ancestorsMap, highlightedCategoryUuid]
  );

  const initialRules = useMemo(
    () => getInitialRules(chartOfAccounts, editingSet),
    [chartOfAccounts, editingSet]
  );

  const handleSave = async (values: FormValues) => {
    const rulePayloads = Object.values(values.rules)
      .filter((r) => r.formula !== FORMULA.NO_RULE)
      .map(buildRulePayload);

    try {
      let result: ForecastSettings;

      if (isEditMode && editingSet) {
        result = await updateForecastSettings({
          entityId,
          settingsId: editingSet.uuid,
          name: values.name.trim(),
          rules: rulePayloads,
        }).unwrap();
        successToast({ message: "Assumption set updated" });
      } else {
        result = await createForecastSettings({
          entityId,
          name: values.name.trim(),
          rules: rulePayloads,
        }).unwrap();
        successToast({ message: "Assumption set created" });
      }

      onCreated?.(result);
      onClose();
    } catch (error) {
      alertToast({
        message: (error as BackendError)?.data?.error?.message,
      });
    }
  };

  const modalTitle = isEditMode ? "Edit assumption set" : "New assumption set";
  const submitLabel = isEditMode ? "Update" : "Save";

  return (
    <Modal.Root
      open={isOpen}
      onOpenChange={(open) => !open && onClose()}
      modal={false}
    >
      <Modal.Content asChild useCustomOverlay size="xl">
        <FormikForm
          key={`${editingSet?.uuid ?? "new"}-${chartOfAccounts.length}`}
          initialValues={{ name: editingSet?.name ?? "", rules: initialRules }}
          validationSchema={validationSchema}
          validateOnMount
          enableReinitialize
          onSubmit={handleSave}
        >
          {({ submitForm, values, setFieldValue, isValid }) => {
            const updateRule = (uuid: string, patch: Partial<RuleRow>) => {
              const next = { ...values.rules };
              next[uuid] = { ...next[uuid], ...patch };
              for (const childUuid of descendantsMap.get(uuid) ?? []) {
                next[childUuid] = { ...next[childUuid], ...patch };
              }
              setFieldValue("rules", next);
            };

            return (
              <>
                <Modal.Header>
                  <Modal.Title>{modalTitle}</Modal.Title>
                  <Modal.Close />
                </Modal.Header>

                <Modal.Body>
                  <div className="t-flex t-flex-col">
                    <TextInput
                      name="name"
                      label="Name"
                      placeholder="e.g. revenue increase by 12%..."
                      required
                    />

                    <div className="t-flex t-items-center t-gap-1.5 t-mt-4">
                      <span className="t-text-body-sm t-text-text-60">
                        Rules
                      </span>
                      <ToolTip
                        text={
                          <div className="t-flex t-flex-col t-gap-1 t-text-body-sm t-text-left">
                            <p>
                              Changing a{" "}
                              <span className="t-font-medium">
                                parent category
                              </span>{" "}
                              rule applies it to all its children.
                            </p>
                            <p>
                              Changing a{" "}
                              <span className="t-font-medium">
                                child category
                              </span>{" "}
                              rule resets its parent to the default (1x), while
                              siblings keep the parent rule.
                            </p>
                          </div>
                        }
                        side="right"
                        propClass="t-max-w-64"
                      >
                        <span className="t-flex t-text-text-30 t-justify-center t-items-center">
                          <SmallInfo className="t-size-4" />
                        </span>
                      </ToolTip>
                    </div>

                    <Async.Root
                      isLoading={isLoadingCOA}
                      isSuccess={!isLoadingCOA}
                      isEmpty={false}
                    >
                      <Async.Success>
                        <div className="t-border t-border-neutral-0 t-rounded-lg t-overflow-hidden t-mt-2">
                          <CategoryList
                            categories={chartOfAccounts}
                            rules={values.rules}
                            onUpdateRule={updateRule}
                            depth={0}
                            expandedUuids={expandedUuids}
                            highlightedCategoryUuid={
                              highlightedCategoryUuid ?? null
                            }
                          />
                        </div>
                      </Async.Success>
                    </Async.Root>
                  </div>
                </Modal.Body>

                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button customType="secondary" type="button">
                      Cancel
                    </Button>
                  </Modal.RawClose>
                  <Button
                    customType="primary"
                    onClick={submitForm}
                    isLoading={isLoading}
                    disabled={isLoading || !isValid}
                    type="button"
                  >
                    {submitLabel}
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            );
          }}
        </FormikForm>
      </Modal.Content>
    </Modal.Root>
  );
};

const FormulaExampleTip = ({
  formula,
  value,
}: {
  formula: FormulaValue;
  value: string;
}) => {
  const info = FORMULA_INFO[formula];
  if (!info) return null;

  const example = info.getExample(value || undefined);

  return (
    <ToolTip
      text={
        <div className="t-flex t-flex-col t-gap-1 t-text-body-sm t-text-left">
          <p>{info.description}</p>
          <div className="t-flex t-flex-col t-gap-1 t-border-t t-border-neutral-0 ">
            <div className="t-flex t-flex-col t-gap-0.5">
              <span className="t-shrink-0">Actual:</span>
              <span className="t-text-text-30 ">{example.actual}</span>
            </div>
            <div className="t-flex t-flex-col t-gap-0.5">
              <span className="t-shrink-0">Forecasted:</span>
              <span className="t-text-text-30 ">{example.forecasted}</span>
            </div>
          </div>
        </div>
      }
      side="top"
      sideOffset={4}
      propClass="t-max-w-72 sm:t-max-w-52"
    >
      <span className="t-flex t-text-text-30 t-items-center t-shrink-0 t-cursor-pointer hover:t-text-text-60 t-transition sm:t-mt-2">
        <BulbIcon />
      </span>
    </ToolTip>
  );
};

const DEPTH_INDENT = 16;
const MOBILE_DEPTH_INDENT = 8;
// Width of the expand arrow plus its gap, so mobile rule fields line up with the account name.
const ARROW_WITH_GAP = 20;

type CategoryListProps = {
  categories: TxnCategories[];
  rules: Record<string, RuleRow>;
  onUpdateRule: (uuid: string, patch: Partial<RuleRow>) => void;
  depth: number;
  expandedUuids: Set<string>;
  highlightedCategoryUuid: string | null;
};

const CategoryList = ({
  categories,
  rules,
  onUpdateRule,
  depth,
  expandedUuids,
  highlightedCategoryUuid,
}: CategoryListProps) => (
  <>
    {categories.map((cat) => (
      <CategoryRow
        key={cat.uuid}
        category={cat}
        rules={rules}
        onUpdateRule={onUpdateRule}
        depth={depth}
        expandedUuids={expandedUuids}
        highlightedCategoryUuid={highlightedCategoryUuid}
      />
    ))}
  </>
);

type CategoryRowProps = Omit<CategoryListProps, "categories"> & {
  category: TxnCategories;
};

const CategoryRow = ({
  category,
  rules,
  onUpdateRule,
  depth,
  expandedUuids,
  highlightedCategoryUuid,
}: CategoryRowProps) => {
  const { isMobile } = useWindowSize();
  const hasChildren = Boolean(category.types?.length);
  const isHighlighted = highlightedCategoryUuid === category.uuid;
  const isRestricted = isRuleRestrictedCategory(category);
  const [isExpanded, setIsExpanded] = useState(
    expandedUuids.has(category.uuid)
  );
  const rule = rules[category.uuid];
  const depthIndent = isMobile ? MOBILE_DEPTH_INDENT : DEPTH_INDENT;

  return (
    <>
      <div
        className={classNames(
          "t-flex t-items-center t-gap-4 t-px-1 sm:t-px-0 t-py-2 t-border-b t-border-neutral-0 last:t-border-b-0 sm:t-flex-col sm:t-items-stretch sm:t-gap-2",
          {
            "t-cursor-pointer hover:t-bg-surface-lighter-grey": hasChildren,
            "animate-transaction-row": isHighlighted,
          }
        )}
        onClick={() => hasChildren && setIsExpanded((prev) => !prev)}
      >
        <div className="t-flex t-items-center t-gap-1 t-w-60 t-shrink-0 sm:t-w-full">
          <div style={{ width: `${depth * depthIndent}px`, flexShrink: 0 }} />
          <div
            className={classNames(
              "t-shrink-0 t-text-text-30 t-transition t-duration-200 t-ease-in-out",
              {
                "t-rotate-90": isExpanded,
                "t-invisible": !hasChildren,
              }
            )}
          >
            <ArrowRight stroke="1.5" color="currentColor" />
          </div>
          <span
            className={classNames("t-text-body t-truncate", {
              "t-font-medium t-text-text-100": depth === 0,
              "t-text-text-60": depth === 1,
              "t-text-text-30": depth >= 2,
            })}
          >
            {category.name}
          </span>
        </div>

        {rule && (
          <div
            className="t-flex t-flex-1 t-items-center t-gap-2 sm:t-min-w-0 sm:t-items-start"
            style={{
              paddingLeft: isMobile
                ? depth * depthIndent + ARROW_WITH_GAP
                : undefined,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="t-flex-1 sm:t-min-w-0">
              <ForecastRuleFields
                layout={isMobile ? "column" : "row"}
                formula={rule.formula}
                value={rule.value}
                startDate={rule.startDate}
                endDate={rule.endDate}
                disabled={isRestricted}
                onFormulaChange={(formula, value) =>
                  onUpdateRule(category.uuid, { formula, value })
                }
                onValueChange={(value) =>
                  onUpdateRule(category.uuid, { value })
                }
                onStartDateChange={(startDate) =>
                  onUpdateRule(category.uuid, { startDate })
                }
                onEndDateChange={(endDate) =>
                  onUpdateRule(category.uuid, { endDate })
                }
              />
            </div>
            <FormulaExampleTip formula={rule.formula} value={rule.value} />
          </div>
        )}
      </div>

      <AnimatePresence>
        {hasChildren && isExpanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="t-overflow-hidden"
          >
            <CategoryList
              categories={category.types!}
              rules={rules}
              onUpdateRule={onUpdateRule}
              depth={depth + 1}
              expandedUuids={expandedUuids}
              highlightedCategoryUuid={highlightedCategoryUuid}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
